
import {
  Box,
  CircularProgress,
  Container,
  GlobalStyles,
  Stack,
  Typography,
  Snackbar,
  Chip,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";

import AppStatusIsland from "./components/AppStatusIsland";
import { GlobalIndustrialBackdrop } from "./components/IndustrialBackdrop";
import FastScrollThumb from "./components/FastScrollThumb";
import PwaInstallOnboarding from "./components/PwaInstallOnboarding";
import { registerCurrentDevice } from "./services/deviceService";
import { loadAllocationMasterData } from "./services/allocationMasterDataService";
import { loadMaterialMasterData } from "./services/materialMasterDataService";
import { loadStorageOwnershipData } from "./services/storageOwnershipDataService";
import { fetchUserNames } from "./services/settingsService";
import { warmWithdrawalNumbering } from "./services/withdrawalIdentityService";
import { useSyncQueue } from "./hooks/useSyncQueue";
import LoginPage from "./pages/LoginPage";
import PasswordChangePage from "./pages/PasswordChangePage";
import ScanWorkflowPage from "./pages/ScanWorkflowPage";
import {
  clearLastKnownAuthUser,
  completePendingServerLogout,
  getCurrentUser,
  getLastKnownAuthUser,
  hasPendingServerLogout,
  type AuthUser,
} from "./services/authService";
import { AppStatusProvider } from "./status/AppStatusContext";
import { APP_UI } from "./ui/appUi";
import { getSyncFailureSimulation, syncHiddenDiagnosticHistoryToServer } from "./services/syncFailureSimulationService";
import { refreshActiveVisualLabProfileBestEffort } from "./services/visualLabService";
import { refreshProductionDeviceRoute } from "./services/productionDeviceRouterService";
import { installPendingMasterDataDraftSyncTriggers } from "./services/masterDataDraftService";
import {
  failRuntimeActivity,
  finishRuntimeActivity,
  queueRuntimeActivity,
  startRuntimeActivity,
} from "./services/runtimeActivityService";

function AppGlobalStyles() {
  return (
    <GlobalStyles
      styles={{
        "html, body, #root": {
          scrollbarWidth: "none",
          WebkitUserSelect: "none",
          userSelect: "none",
          WebkitTouchCallout: "none",
          overscrollBehavior: "none",
          overscrollBehaviorY: "none",
          backgroundColor: "#121417",
          backgroundImage: "none",
        },
        body: {
          position: "relative",
          minHeight: "100dvh",
          overflowX: "hidden",
          isolation: "isolate",
        },
        "#root": {
          position: "relative",
          minHeight: "100dvh",
          isolation: "isolate",
          backgroundColor: "transparent",
        },
        "*": {
          scrollbarWidth: "none",
          WebkitUserSelect: "none",
          userSelect: "none",
          WebkitTouchCallout: "none",
          WebkitTapHighlightColor: "transparent",
        },
        "*::-webkit-scrollbar": {
          display: "none",
          width: 0,
          height: 0,
        },
        "input, textarea, [contenteditable='true']": {
          WebkitUserSelect: "text",
          userSelect: "text",
          WebkitTouchCallout: "default",
        },
      }}
    />
  );
}

function findScrollableAncestor(target: EventTarget | null): HTMLElement | null {
  let element = target instanceof HTMLElement ? target : null;

  while (element && element !== document.body && element !== document.documentElement) {
    const style = window.getComputedStyle(element);
    const overflowY = style.overflowY;
    const canScrollY =
      (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
      element.scrollHeight > element.clientHeight + 1;

    if (canScrollY) return element;
    element = element.parentElement;
  }

  return null;
}

function usePreventBrowserPullToRefresh(): void {
  useEffect(() => {
    let touchStartY: number | null = null;

    const handleTouchStart = (event: TouchEvent) => {
      touchStartY = event.touches.length === 1 ? event.touches[0].clientY : null;
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (touchStartY === null || event.touches.length !== 1) return;

      const deltaY = event.touches[0].clientY - touchStartY;
      if (deltaY <= 0) return;

      // Wenn eine echte Scrollfläche noch oberhalb Inhalt besitzt, bleibt ihr
      // normales Scrollen unangetastet. Nur Overscroll am oberen Rand wird
      // abgefangen, bevor Safari/der Browser daraus Pull-to-Refresh macht.
      const scrollable = findScrollableAncestor(event.target);
      if (scrollable && scrollable.scrollTop > 0) return;

      if (!scrollable) {
        const pageScrollTop =
          window.scrollY ||
          document.documentElement.scrollTop ||
          document.body.scrollTop;
        if (pageScrollTop > 0) return;
      }

      // Nur die Browser-Standardaktion unterdrücken. Die Touch-Events selbst
      // laufen weiter, sodass Swipe-to-close von Top-/Bottom-Sheets sowie
      // eigene Gesten weiterhin funktionieren.
      event.preventDefault();
    };

    const resetTouch = () => {
      touchStartY = null;
    };

    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", resetTouch, { passive: true });
    document.addEventListener("touchcancel", resetTouch, { passive: true });

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", resetTouch);
      document.removeEventListener("touchcancel", resetTouch);
    };
  }, []);
}

function SessionCheckScreen({ delayedHint = false }: { delayedHint?: boolean }) {
  return (
    <Box
      sx={{
        minHeight: "100dvh",
        bgcolor: "transparent",
        color: "#f5f5f7",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Stack spacing={1.3} sx={{ alignItems: "center" }}>
        <Box
          component="img"
          src="/icons/materialentnahme-logo-192.png"
          alt="Materialentnahme Logo"
          sx={{ width: 82, height: 82, objectFit: "contain" }}
        />
        <CircularProgress size={25} sx={{ color: "#83cee0" }} />
        <Typography variant="caption" sx={{ color: "#8f9196" }}>
          {delayedHint ? "Serverantwort dauert länger · lokale Sitzung wird vorbereitet …" : "Sitzung wird geprüft …"}
        </Typography>
        {delayedHint && <Chip size="small" label="Serverantwort verzögert" sx={{ color: APP_UI.warning, borderColor: "rgba(244,163,64,.35)", bgcolor: "rgba(244,163,64,.08)" }} variant="outlined" />}
      </Stack>
    </Box>
  );
}



function AppShell() {
  useEffect(() => installPendingMasterDataDraftSyncTriggers(), []);
  useSyncQueue();
  useEffect(() => {
    // Best-Effort-Schutz gegen aggressive Speicherbereinigung mobiler Browser.
    // Fehlende deterministisch erzeugbare Dokumente werden zusätzlich beim
    // Öffnen bzw. vor einem Sync aus den fachlichen Entnahmedaten rekonstruiert.
    if (!navigator.storage?.persist) return;
    void navigator.storage.persist().catch(() => false);
  }, []);
  const [isStartPage, setIsStartPage] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const [diagnosticSimulationActive, setDiagnosticSimulationActive] = useState(() => getSyncFailureSimulation().enabled);

  useEffect(() => {
    const flushDiagnosticTombstones = () => { void syncHiddenDiagnosticHistoryToServer().catch(() => undefined); };
    flushDiagnosticTombstones();
    window.addEventListener("online", flushDiagnosticTombstones);
    window.addEventListener("materialentnahme:status-refresh", flushDiagnosticTombstones);
    return () => {
      window.removeEventListener("online", flushDiagnosticTombstones);
      window.removeEventListener("materialentnahme:status-refresh", flushDiagnosticTombstones);
    };
  }, []);

  useEffect(() => {
    let lastRefreshAt = 0;
    const refreshProductionRuntime = () => {
      const now = Date.now();
      if (now - lastRefreshAt < 2500) return;
      lastRefreshAt = now;
      void refreshActiveVisualLabProfileBestEffort();
      // v0.298: serverseitige Production-Device-Routing-Entscheidung wird als
      // fail-safe Sidecar vorgewärmt. Die bestehende Production Recognition bleibt
      // byte-identisch; bei Offline/Fehlern existiert ausschließlich canonical Production.
      void refreshProductionDeviceRoute();
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") refreshProductionRuntime();
    };

    // Cross-device Production-Konvergenz: App-Start, Rueckkehr in die PWA und
    // Wiederverbindung aktualisieren den letzten bekannten Vision-Stand best-effort.
    // Kein Pfad blockiert den Offline-First-Start oder eine laufende Aufnahme.
    refreshProductionRuntime();
    window.addEventListener("online", refreshProductionRuntime);
    window.addEventListener("focus", refreshProductionRuntime);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("online", refreshProductionRuntime);
      window.removeEventListener("focus", refreshProductionRuntime);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  useEffect(() => {
    const refreshDiagnosticMode = () => setDiagnosticSimulationActive(getSyncFailureSimulation().enabled);
    window.addEventListener("materialentnahme:sync-simulation-updated", refreshDiagnosticMode);
    return () => window.removeEventListener("materialentnahme:sync-simulation-updated", refreshDiagnosticMode);
  }, []);

  useEffect(() => {
    const onToast = (event: Event) => {
      const message = (event as CustomEvent<{ message?: string }>).detail?.message;
      if (message) setToast(message);
    };
    window.addEventListener("materialentnahme:toast", onToast);
    return () => window.removeEventListener("materialentnahme:toast", onToast);
  }, []);

  useEffect(() => {
    const standalone = window.matchMedia?.("(display-mode: standalone)").matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
    if (!standalone) return;
    const state = typeof window.history.state === "object" && window.history.state !== null ? window.history.state : {};
    if (!(state as Record<string, unknown>).__materialentnahme_root_guard) {
      window.history.pushState({ ...state, __materialentnahme_root_guard: true }, "", window.location.href);
    }
  }, []);

  useEffect(() => {
    if (window.location.pathname !== "/vision-optimizer") return;
    const targetUrl = `${window.location.pathname}${window.location.search}`;
    const timer = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("materialentnahme:navigate-target", { detail: { targetUrl } }));
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const withdrawalId = Number(params.get("withdrawalId") ?? "");
    const withdrawalUuid = (params.get("withdrawalUuid") ?? "").trim();
    const hasServerId = Number.isFinite(withdrawalId) && withdrawalId > 0;
    const hasLocalUuid = withdrawalUuid.length > 0;
    if (!hasServerId && !hasLocalUuid) return;
    const timer = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("materialentnahme:open-history-entry", { detail: { withdrawalId: hasServerId ? withdrawalId : undefined, clientUuid: hasLocalUuid ? withdrawalUuid : undefined } }));
      params.delete("withdrawalId");
      params.delete("withdrawalUuid");
      const query = params.toString();
      window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
    }, 250);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <Box
      sx={{
        minHeight: isStartPage ? "100dvh" : "100dvh",
        height: isStartPage ? "100dvh" : "auto",
        overflow: isStartPage ? "hidden" : "visible",
        pt: isStartPage
          ? "calc(58px + env(safe-area-inset-top))"
          : "max(0.45rem, env(safe-area-inset-top))",
        boxSizing: "border-box",
        backgroundColor: "transparent",
        color: "#f5f5f7",
      }}
    >
      {isStartPage && <AppStatusIsland />}
      {diagnosticSimulationActive && <Chip size="small" clickable label="Diagnosemodus aktiv" onClick={() => window.dispatchEvent(new Event("materialentnahme:navigate-failure-simulation"))} sx={{ position: "fixed", top: "calc(8px + env(safe-area-inset-top))", right: 8, zIndex: 2200, color: "#f4a340", borderColor: "rgba(244,163,64,.45)", bgcolor: "rgba(27,27,29,.92)", fontWeight: 900, cursor: "pointer" }} variant="outlined" />}

      <Container
        maxWidth="sm"
        sx={{
          px: { xs: 1.5, sm: 2 },
          pt: isStartPage ? 1.2 : 0.45,
          pb: isStartPage ? 0 : 4,
          height: isStartPage
            ? "calc(100dvh - 58px - env(safe-area-inset-top))"
            : "auto",
          minHeight: isStartPage
            ? 0
            : "calc(100dvh - max(0.45rem, env(safe-area-inset-top)))",
          overflow: isStartPage ? "hidden" : "visible",
          boxSizing: "border-box",
          backgroundColor: "transparent",
        }}
      >
        <ScanWorkflowPage onStartPageChange={setIsStartPage} />
      </Container>

      {!isStartPage && <FastScrollThumb />}
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={2600}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          left: "50%",
          right: "auto",
          bottom: "calc(max(1rem, env(safe-area-inset-bottom)) + 5.25rem)",
          transform: "translateX(-50%)",
          width: "min(calc(100vw - 2rem), 34rem)",
        }}
      >
        <Box
          sx={{
            width: "100%",
            minHeight: 58,
            px: 2,
            py: 1.15,
            display: "flex",
            alignItems: "center",
            gap: 1.35,
            borderRadius: "999px",
            boxSizing: "border-box",
            color: "#f4f5f7",
            background: "rgba(39, 40, 43, 0.96)",
            border: "1px solid rgba(255,255,255,0.035)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.38)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
          }}
        >
          <Box
            component="img"
            src="/icons/materialentnahme-logo-192.png"
            alt=""
            aria-hidden="true"
            sx={{
              width: 38,
              height: 38,
              flex: "0 0 38px",
              objectFit: "contain",
              borderRadius: "50%",
            }}
          />
          <Typography
            sx={{
              minWidth: 0,
              color: "inherit",
              fontWeight: 500,
              fontSize: "clamp(0.96rem, 3.8vw, 1.08rem)",
              lineHeight: 1.25,
            }}
          >
            {toast ?? ""}
          </Typography>
        </Box>
      </Snackbar>
    </Box>
  );
}

const USER_READY_KEY_PREFIX = "materialentnahme:user-ready:";
const INSTALL_ONBOARDING_DONE_PREFIX = "materialentnahme:install-onboarding-done:";

function userReadyKey(userId: number): string { return `${USER_READY_KEY_PREFIX}${userId}`; }
function installDoneKey(userId: number): string { return `${INSTALL_ONBOARDING_DONE_PREFIX}${userId}`; }

function hasLocalMarker(key: string): boolean {
  try { return window.localStorage.getItem(key) === "1"; } catch { return false; }
}

function setLocalMarker(key: string): void {
  try { window.localStorage.setItem(key, "1"); } catch { /* optional */ }
}

function isInstalledPwa(): boolean {
  return window.matchMedia?.("(display-mode: standalone)").matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
}

function scheduleStartupTask(
  id: string,
  label: string,
  task: () => Promise<unknown> | unknown,
  delayMs: number,
  idleTimeoutMs = 1800,
): () => void {
  let idleId: number | null = null;
  let cancelled = false;
  const idleWindow = window as typeof window & {
    requestIdleCallback?: (callback: () => void, options?: { timeout?: number }) => number;
    cancelIdleCallback?: (id: number) => void;
  };
  queueRuntimeActivity(id, label, "startup", `wartet auf Idle-Zeit · frühestens ${delayMs} ms`);
  const run = () => {
    if (cancelled) return;
    startRuntimeActivity(id, label, "startup");
    Promise.resolve()
      .then(task)
      .then(() => finishRuntimeActivity(id))
      .catch((error) => failRuntimeActivity(id, error));
  };
  const timer = window.setTimeout(() => {
    if (idleWindow.requestIdleCallback) idleId = idleWindow.requestIdleCallback(run, { timeout: idleTimeoutMs });
    else run();
  }, delayMs);
  return () => {
    cancelled = true;
    window.clearTimeout(timer);
    if (idleId !== null) idleWindow.cancelIdleCallback?.(idleId);
  };
}

export default function App() {
  usePreventBrowserPullToRefresh();

  const [sessionState, setSessionState] =
    useState<"checking" | "authenticated" | "anonymous">("checking");
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [installOnboardingUserId, setInstallOnboardingUserId] = useState<number | null>(null);
  const [sessionDelayedHint, setSessionDelayedHint] = useState(false);
  const [serverSessionState, setServerSessionState] = useState<"checking" | "confirmed" | "unavailable" | "rejected">("checking");

  const checkSession = useCallback(async () => {
    setSessionDelayedHint(false);
    setServerSessionState("checking");

    // Ein local-first Logout kann bei einem kurzen Netz-/Serverfehler noch eine
    // gültige Server-Cookie-Sitzung hinterlassen. Solange dieser Logout nicht
    // serverseitig bestätigt ist, darf /me die alte Sitzung nicht wiederbeleben.
    if (hasPendingServerLogout()) {
      const completed = await completePendingServerLogout(2500);
      if (!completed) {
        setCurrentUser(null);
        setSessionState("anonymous");
        setServerSessionState("unavailable");
        return;
      }
    }

    const cachedUser = getLastKnownAuthUser();

    // Offline-first Session-Boot: Eine bereits bekannte lokale Sitzung darf die
    // Startseite sofort freigeben. Die authoritative Serverprüfung läuft direkt
    // danach weiter. Eine echte 401-Antwort widerruft den Cache wie bisher; bei
    // Timeout/Netzwerkfehler bleibt das bisherige Offline-Verhalten erhalten.
    if (cachedUser) {
      setCurrentUser(cachedUser);
      setSessionState("authenticated");
    }

    const delayedHintTimer = cachedUser
      ? null
      : window.setTimeout(() => setSessionDelayedHint(true), 700);
    try {
      const user = await getCurrentUser();
      if (user && !user.must_change_password) setLocalMarker(userReadyKey(user.id));
      setCurrentUser(user);
      setSessionState(user ? "authenticated" : "anonymous");
      setServerSessionState(user ? "confirmed" : "rejected");
    } catch {
      setServerSessionState("unavailable");
      if (!cachedUser) {
        setCurrentUser(null);
        setSessionState("anonymous");
      }
    } finally {
      if (delayedHintTimer !== null) window.clearTimeout(delayedHintTimer);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void checkSession();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [checkSession]);

  useEffect(() => {
    if (sessionState !== "authenticated" || !currentUser || serverSessionState !== "confirmed") return;

    // Die Startseite soll zuerst interaktiv sein. Große IndexedDB-/Index-Aufbauten
    // und Best-Effort-Preloads werden danach gestaffelt in Idle-Zeit ausgeführt.
    // Fachlich ändert sich nichts: Jede Zielseite kann ihre Daten weiterhin selbst
    // laden; die Aufgaben hier sind ausschließlich Warmups.
    const cleanups = [
      scheduleStartupTask("startup:device", "Gerät registrieren", () => registerCurrentDevice(), 0, 700),
      scheduleStartupTask("startup:numbering", "Entnahmenummer vorwärmen", () => warmWithdrawalNumbering(currentUser.id), 120, 900),
      scheduleStartupTask("startup:allocations", "Kontierungen vorladen", () => loadAllocationMasterData(), 450, 1400),
      scheduleStartupTask("startup:ownership", "Ordnungspatenschaften vorladen", () => loadStorageOwnershipData(), 700, 1800),
      scheduleStartupTask("startup:usernames", "Benutzernamen vorladen", () => fetchUserNames(), 900, 2000),
      scheduleStartupTask("startup:materials", "Materialstamm indexieren", () => loadMaterialMasterData(), 1200, 2800),
    ];
    return () => cleanups.forEach((cleanup) => cleanup());
  }, [sessionState, currentUser, serverSessionState]);

  useEffect(() => {
    const finishPendingLogout = () => {
      if (hasPendingServerLogout()) void completePendingServerLogout(2500);
    };
    window.addEventListener("online", finishPendingLogout);
    return () => window.removeEventListener("online", finishPendingLogout);
  }, []);

  useEffect(() => {
    const revalidateUnavailableSession = () => {
      if (serverSessionState === "unavailable") void checkSession();
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") revalidateUnavailableSession();
    };
    window.addEventListener("online", revalidateUnavailableSession);
    window.addEventListener("focus", revalidateUnavailableSession);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("online", revalidateUnavailableSession);
      window.removeEventListener("focus", revalidateUnavailableSession);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [checkSession, serverSessionState]);

  useEffect(() => {
    const handleLogout = () => {
      setCurrentUser(null);
      setSessionState("anonymous");
      setServerSessionState("rejected");
    };
    const handleAuthInvalid = () => {
      clearLastKnownAuthUser();
      handleLogout();
    };
    window.addEventListener("materialentnahme:logout", handleLogout);
    window.addEventListener("materialentnahme:auth-invalid", handleAuthInvalid);
    return () => {
      window.removeEventListener("materialentnahme:logout", handleLogout);
      window.removeEventListener("materialentnahme:auth-invalid", handleAuthInvalid);
    };
  }, []);

  return (
    <>
      <AppGlobalStyles />
      <GlobalIndustrialBackdrop />

      <Box sx={{ position: "relative", zIndex: 1, minHeight: "100dvh" }}>
      {sessionState === "checking" && <SessionCheckScreen delayedHint={sessionDelayedHint} />}

      {sessionState === "anonymous" && (
        <LoginPage
          onLogin={(user) => {
            if (!user.must_change_password) setLocalMarker(userReadyKey(user.id));
            setCurrentUser(user);
            setSessionState("authenticated");
            setServerSessionState("confirmed");
          }}
        />
      )}

      {sessionState === "authenticated" && currentUser?.must_change_password && (
        <PasswordChangePage
          user={currentUser}
          onChanged={(changedUser) => {
            const wasKnownReady = hasLocalMarker(userReadyKey(currentUser.id));
            const explicitlyInitial = currentUser.password_change_reason === "initial";
            const explicitlyReset = currentUser.password_change_reason === "reset";
            const shouldOfferInstall =
              !isInstalledPwa() &&
              !hasLocalMarker(installDoneKey(currentUser.id)) &&
              !explicitlyReset &&
              (explicitlyInitial || !wasKnownReady);

            setLocalMarker(userReadyKey(currentUser.id));
            setCurrentUser(changedUser);
            if (shouldOfferInstall) setInstallOnboardingUserId(currentUser.id);
          }}
        />
      )}

      {sessionState === "authenticated" && currentUser && !currentUser.must_change_password && (
        <AppStatusProvider serverSessionState={serverSessionState}>
          <AppShell />
        </AppStatusProvider>
      )}

      {sessionState === "authenticated" && currentUser && !currentUser.must_change_password && (
        <PwaInstallOnboarding
          open={installOnboardingUserId === currentUser.id}
          onClose={() => {
            setLocalMarker(installDoneKey(currentUser.id));
            setInstallOnboardingUserId(null);
          }}
        />
      )}
      </Box>
    </>
  );
}
